/**
 * Contrato del contexto de pantalla que viaja del SaaS a Mindy.
 *
 * Referencia: ANALISIS_MINDY_CONTEXTUAL_SAAS.md §B.10.2, PLAN_MINDY_CONTEXTUAL_SAAS.md
 * Capa 0. No cambiar esto sin revisar esos documentos: las dos decisiones de diseño de
 * acá abajo (scope por bloque, view además de app) no son arbitrarias.
 */

export const MINDY_CONTEXT_VERSION = 1;
export const MINDY_CONTEXT_CHANNEL = 'mindy-context';
export const MINDY_CONTEXT_STORAGE_KEY = 'mindy:ctx';

/** Caps de tamaño. Ver README para el motivo de truncar en vez de recortar. */
export const MAX_BLOCK_BYTES = 8_192;
export const MAX_CONTEXT_BYTES = 24_576;
export const MAX_VISIBLE_ROWS = 20;

/**
 * Alcance de un bloque de datos. Es el campo más importante del contrato: sin esto,
 * Mindy no puede distinguir un número sin filtrar de uno filtrado de uno paginado, y
 * las tres respuestas parecen igual de razonables desde adentro del prompt (ver README).
 */
export type BlockScope =
    /** Calculado sobre el rango de fechas, SIN aplicar los filtros de la vista (QS-8047). */
    | 'date_range_unfiltered'
    /** Calculado con los filtros de la vista aplicados. */
    | 'filtered'
    /** Sólo las filas de la página actual. */
    | 'visible_page';

export interface ContextBlock {
    key: 'routes_summary' | 'routes_list' | 'dashboard_routes' | 'dashboard_stats' | 'orders_list';
    scope: BlockScope;
    /**
     * Query params con los que se pidió este bloque. Habilita el chequeo de coherencia
     * en el backend (si el bloque se pidió con una fecha distinta a la que declaran los
     * filtros actuales, se descarta por viejo). Objeto vacío si no se usó ningún param.
     */
    params: Record<string, string | number>;
    fetched_at: string; // ISO-8601 con offset
    /**
     * Payload del BFF, verbatim y recortado. El mapeo a nombres semánticos lo hace el
     * formateador del backend, no el front — así un cambio de shape del BFF degrada
     * (el formateador ignora lo que no reconoce) en vez de romper.
     * `null` si el bloque excedió MAX_BLOCK_BYTES.
     */
    payload: unknown | null;
    truncated?: true;
}

/**
 * Filtros de la vista, en valores canónicos (los mismos que `RouteStatus`/`OrderStatus`
 * en libs/shared/route-tracking), nunca los labels que muestra `FilterTranslate` en la UI.
 *
 * Todos los campos son opcionales a propósito: no todas las vistas soportan todos los
 * filtros (p. ej. `/dashboard/routes` no acepta filtros de estado, ver B.10.3) y no todas
 * las pantallas envían todos los parámetros. Un campo ausente significa "esta vista no
 * expone este filtro o el usuario no lo aplicó", nunca "el valor es vacío".
 */
export interface ViewFilters {
    date_from?: string; // YYYY-MM-DD
    date_to?: string;
    date_label?: string; // "Hoy", "Últimos 7 días" — tal como lo muestra la UI
    route_status?: string; // valor canónico: pending|ongoing|extended|finished|closed
    order_status?: string; // valor canónico: pending|failed|done|partial|not-loaded|closed
    drivers?: string;
    vehicles?: string;
    reprogrammed?: boolean;
    search?: string;
    sort_by?: string;
    sort_direction?: 'ASC' | 'DESC';
}

export interface MindyViewContext {
    context_version: typeof MINDY_CONTEXT_VERSION;
    app: 'route-tracking';
    /** Necesario además de `app`: los filtros disponibles cambian por vista (ver B.10.3). */
    view: 'routes' | 'dashboard' | 'orders' | 'route-detail';
    captured_at: string;
    /** El "Última Actualización HH:mm" que muestra la pantalla. `null` si no aplica. */
    data_as_of: string | null;
    /** `null` si la vista no tiene ningún filtro activo (distinto de "no soporta filtros"). */
    filters: ViewFilters | null;
    blocks: ContextBlock[];
    selection?: { route_id?: number };
}
